import React, { useEffect, useReducer, useRef, useState } from 'react';
import {
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  FormHelperText,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import CancelIcon from '@material-ui/icons/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Attachment } from '@material-ui/icons';
import CloseIcon from '@mui/icons-material/Close';
import { notificationManager } from '../../../../utils/NotificationUtils';
import { decryptLocalStorage } from '../../../../utils/authUtils';
import { USER } from '../../../../constants/CommonConst';
import EmailServiceTemplateTab from '../guest/guest-create/EmailServiceTemplateTab';
import EmailServiceAudienceTab from '../guest/guest-create/EmailServiceAudienceTab';
import {
  allRoomsSelector,
  allRoomTypesSelector,
  fetchAllAccommodations,
} from '../../../property/propertySlice';
import { sendEmail } from '../../../../utils/cmsUtils';
import { getPreSignedUrl } from '../../../../utils/PropertyAPIUtils';
import {
  abortMultipartUpload,
  calculateFileChecksum,
  calculateTotalChunks,
  CHUNK_SIZE,
  completeMultipartUpload,
  convertFileToBase64,
  sanitizeFileName,
} from '../../../../utils/commonUtils';
import { scanPdfSecurity } from '../../../../utils/pdfSecurityUtils';
import { formDataValidate, handleValidation } from '../../../../utils/formUtils';
import { PROPERTY_SERVICE_URL } from '../../../../env/Configurations';
import './NewMessageCreate.scss';
import { PictureAsPdfOutlined } from '@material-ui/icons';

const MAX_EMAIL_ATTACHMENT_SIZE = 8 * 1024 * 1024;

const getLoggedInUser = () => {
  try { return decryptLocalStorage(USER) || {}; } catch { return {}; }
};

const wrapBodyAsHtml = (plainText) => {
  const escaped = (plainText || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
  return `<!DOCTYPE html><html><body style="font-family:sans-serif;font-size:14px;color:#333;padding:16px;">${escaped}</body></html>`;
};

const buildAudience = (formData, roomTypeList = []) => {
  const recipientMode = formData?.recipientMode?.value || 'Custom';
  if (recipientMode === 'Custom') {
    const emails = (formData?.toEmails?.value || '')
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    return { type: 'CUSTOM', emails };
  }
  const roomTypeCodes = (formData.roomType?.value || []).map((name) => {
    const match = roomTypeList.find((rt) => rt.name === name);
    return match?.code || name;
  });
  return {
    type: 'GUEST_SEGMENTS',
    guestSegments: {
      rooms: formData.residentRoom?.value || [],
      roomTypes: roomTypeCodes,
      blockCodes: formData.blockCodes?.value || [],
      loyaltyTiers: formData.loyaltyTiers?.value || [],
    },
  };
};

const uploadPdfToS3 = async (file, groupId, onProgress, signal) => {
  await scanPdfSecurity(file);

  const fileSize = file.size;

  if (fileSize < 2 * 1024 * 1024) {
    const base64 = await convertFileToBase64(file);
    onProgress(50);
    const res = await getPreSignedUrl(
      {
        contentLength: file.size,
        contentType: file.type,
        filename: sanitizeFileName(file.name),
        skipContentValidation: false,
        isDocUpload: true,
        contents: base64,
        type: 'properties',
        propertyType: 'email-services',
        groupId,
      },
      { signal },
    );
    const key = res?.data?.getPreSignUrlCMS?.key;
    if (!key) throw new Error(`Failed to upload attachment: ${file.name}`);
    onProgress(100);
    return key;
  }

  const totalChunks = calculateTotalChunks(fileSize);
  const fileChecksum = await calculateFileChecksum(file);

  const startRes = await fetch(`${PROPERTY_SERVICE_URL}/startMultipartUpload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      groupId,
      type: 'properties',
      propertyType: 'email-services',
      contentType: file.type,
      filename: sanitizeFileName(file.name),
      skipContentValidation: false,
      IsCMSRequest: true,
      fileSize,
      fileChecksum,
    }),
    signal,
  });
  if (!startRes.ok) throw new Error(`Failed to start upload: ${startRes.status}`);

  const chunkData = await startRes.json();
  const uploadId = chunkData?.data?.key;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, fileSize));
    const base64Chunk = await convertFileToBase64(chunk);

    const chunkRes = await fetch(`${PROPERTY_SERVICE_URL}/uploadMultipartChunk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groupId, uploadId, sequenceNumber: i + 1, contents: base64Chunk }),
      signal,
    });
    if (!chunkRes.ok) throw new Error(`Failed to upload chunk ${i + 1}`);
    onProgress(Math.round(((i + 1) / totalChunks) * 100));
  }

  const completeRes = await completeMultipartUpload(groupId, null, uploadId);
  if (completeRes?.status !== 'ok' || !completeRes?.data?.status) {
    await abortMultipartUpload(uploadId, groupId, null);
    throw new Error('Failed to complete multipart upload');
  }
  return completeRes.data.message;
};

const buildInitialFormData = (defaultFromAddress) => {
  const loggedUser = getLoggedInUser();
  const fromEmail = defaultFromAddress || loggedUser?.email || '';
  const fromName = loggedUser?.firstName
    ? `${loggedUser.firstName} ${loggedUser.lastName || ''}`.trim()
    : 'Hotel';
  return ({
  name: { value: 'New Message', fieldName: 'Name', validations: [], errors: {} },
  fromName: { value: fromName, fieldName: 'From Name', validations: [], errors: {} },
  fromAddress: {
    value: fromEmail,
    fieldName: 'From Address',
    validations: [],
    errors: {},
  },
  cc: { value: '', fieldName: 'CC', validations: [], errors: {} },
  subject: {
    value: '',
    fieldName: 'Subject Line',
    validations: ['required'],
    errors: {},
  },
  subjectBody: {
    value: '',
    fieldName: 'Subject Body',
    validations: ['required'],
    errors: {},
  },
  templateMode: { value: '', fieldName: 'Template Mode', validations: [], errors: {} },
  htmlTemplateBody: { value: '', fieldName: 'HTML Template Body', validations: [], errors: {} },
  pickedTemplate: { value: null, fieldName: 'Picked Template', validations: [], errors: {} },
  recipientMode: { value: 'Custom', fieldName: 'Recipient Mode', validations: [], errors: {} },
  toEmails: { value: '', fieldName: 'To Email Addresses', validations: ['required', 'validEmailList'], errors: {} },
  audienceType: { value: [], fieldName: 'Audience Type', validations: [], errors: {} },
  residentRoom: { value: [], fieldName: 'Rooms', validations: [], errors: {} },
  roomType: { value: [], fieldName: 'Room Type', validations: [], errors: {} },
  blockCodes: { value: [], fieldName: 'Block Codes', validations: ['required'], errors: {} },
  loyaltyTiers: { value: [], fieldName: 'Loyalty Tiers', validations: ['required'], errors: {} },
});
};

const CustomComposeForm = ({ formData, handleInputChange, onBack, attachments, onAttach, onRemoveAttach }) => {
  const subject = formData?.subject?.value || '';
  const subjectBody = formData?.subjectBody?.value || '';
  const subjectErr = formData?.subject?.errors?.required || '';
  const bodyErr = formData?.subjectBody?.errors?.required || '';
  const ccErr = (formData?.cc?.value && formData?.cc?.errors?.validEmailList) || '';
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (file.type !== 'application/pdf') {
        notificationManager('warning', 'Only PDF files are supported.');
        return;
      }
      if (file.size > MAX_EMAIL_ATTACHMENT_SIZE) {
        notificationManager('danger', 'Total attachment size cannot exceed 8 MB.');
        return;
      }
      onAttach(file);
    });
    e.target.value = '';
  };

  return (
    <div className='nm-compose-form'>
      <div className='nm-back-row'>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} className='nm-back-btn' size='small'>
          Back
        </Button>
      </div>

      {formData?.fromAddress?.value && (
        <div className='nm-from-address'>
          <div className='nm-field-label'>From Address</div>
          <div className='nm-field-value'>{formData.fromAddress.value}</div>
        </div>
      )}

      <TextField
        fullWidth
        label='CC Email Addresses (separate with commas)'
        placeholder='john@hotel.com, jane@brand.com'
        value={formData?.cc?.value || ''}
        onChange={(e) => {
          const val = e.target.value;
          const prevCount = (formData.cc?.value || '').split(',').map((s) => s.trim()).filter(Boolean).length;
          const newCount = val.split(',').map((s) => s.trim()).filter(Boolean).length;
          if (prevCount >= 50 && newCount > prevCount) {
            handleInputChange({ cc: { ...formData.cc, errors: { validEmailList: 'You can send to a maximum of 50 email addresses.' } } });
            return;
          }
          const strictEmailRe = /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          const rawCcEntries = val.split(',').map((s) => s.trim());
          const nonEmptyCcEmails = rawCcEntries.filter(Boolean);
          let ccError = null;
          if (nonEmptyCcEmails.length > 50) {
            ccError = 'You can send to a maximum of 50 email addresses.';
          } else if (nonEmptyCcEmails.length > 0 && rawCcEntries.some((em) => !strictEmailRe.test(em))) {
            ccError = 'Invalid email format found in the list.';
          }
          handleInputChange({ cc: { ...formData.cc, value: val, errors: ccError ? { validEmailList: ccError } : {} } });
        }}
        error={Boolean(ccErr)}
        helperText={ccErr || ''}
        className='nm-text-field'
        variant='standard'
      />

      <TextField
        fullWidth
        label='Subject Line'
        required
        value={subject}
        inputProps={{ maxLength: 100 }}
        onChange={(e) =>
          handleInputChange({ subject: { ...formData.subject, value: e.target.value, errors: {} } })
        }
        error={Boolean(subjectErr)}
        helperText={subjectErr || ''}
        className='nm-text-field'
        variant='standard'
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <span className='nm-char-count-adornment'>{subject.length}/100</span>
            </InputAdornment>
          ),
        }}
      />

      <div className={`nm-body-wrapper${bodyErr ? ' nm-body-wrapper--error' : ''}`}>
        {!subjectBody && (
          <div className='nm-body-placeholder' aria-hidden='true'>
            Subject Body <span className='nm-body-required'>*</span>
          </div>
        )}
        <textarea
          className='nm-body-textarea'
          placeholder=''
          value={subjectBody}
          maxLength={1000}
          onChange={(e) =>
            handleInputChange({
              subjectBody: { ...formData.subjectBody, value: e.target.value, errors: {} },
            })
          }
          rows={14}
        />
        <div className='nm-body-footer'>
          <button
            type='button'
            className='nm-attach-btn'
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <Attachment />
            <span>Attach File</span>
          </button>
          <span className='nm-char-count'>{subjectBody.length}/1000</span>
        </div>
        <input
          ref={fileInputRef}
          type='file'
          accept='application/pdf'
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
      {bodyErr && <div className='nm-field-error'>{bodyErr}</div>}

      {attachments?.length > 0 && (
        <div className='nm-attachments'>
          {attachments?.map((att) => (
            <div key={att.id} className={`nm-attachment-pill${att.error ? ' nm-attachment-pill--error' : ''}`}>
              <PictureAsPdfOutlined />
              <div className='nm-attachment-info'>
                <span className='nm-attachment-name'>{att.name}</span>
                {att.uploading && (
                  <LinearProgress
                    variant='determinate'
                    value={att.progress}
                    className='nm-attachment-progress'
                  />
                )}
                {att.error && (
                  <span className='nm-attachment-error'>Upload failed</span>
                )}
              </div>
              <button
                type='button'
                className='nm-attachment-remove'
                onClick={() => onRemoveAttach(att.id)}
                aria-label={`Remove ${att.name}`}
              >
                <CloseIcon fontSize='small' />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const NewMessageAudienceTab = ({ formData, handleInputChange, setFormData, hotelId, roomList, roomTypeList }) => {
  const language = (() => { const l = decryptLocalStorage('lang'); return l !== 'en' ? l : ''; })();
  const recipientMode = formData?.recipientMode?.value || 'Custom';
  const toEmailsErrors = formData?.toEmails?.errors || {};
  const toEmailsErr = toEmailsErrors.required || toEmailsErrors.validEmailList || '';

  const onRecipientModeChange = (val) => {
    handleInputChange({
      recipientMode: { ...formData.recipientMode, value: val, errors: {} },
      toEmails: { ...formData.toEmails, value: '', errors: {} },
      audienceType: { ...formData.audienceType, value: [], errors: {} },
      residentRoom: { ...formData.residentRoom, value: [] },
      roomType: { ...formData.roomType, value: [] },
      blockCodes: { ...formData.blockCodes, value: [] },
      loyaltyTiers: { ...formData.loyaltyTiers, value: [] },
    });
  };

  const guestFormData = { ...formData, recipientType: { value: 'Guests' } };

  return (
    <div className='w-100 mt-4'>
      <RadioGroup row value={recipientMode} onChange={(e) => onRecipientModeChange(e.target.value)}>
        <FormControlLabel value='Custom' control={<Radio color='primary' />} label='Custom' />
        <FormControlLabel value='Guest Segments' control={<Radio color='primary' />} label='Guest Segments' />
      </RadioGroup>

      {recipientMode === 'Custom' && (
        <div className='row w-100 mt-3'>
          <div className='col-md-11'>
            <TextField
              fullWidth
              required
              label='To Email Addresses (separate with commas)'
              placeholder='john@hotel.com, jane@brand.com'
              value={formData?.toEmails?.value || ''}
              onChange={(e) => {
                const val = e.target.value;
                const prevCount = (formData.toEmails?.value || '').split(',').map((s) => s.trim()).filter(Boolean).length;
                const newCount = val.split(',').map((s) => s.trim()).filter(Boolean).length;
                // Block adding more once at 50
                if (prevCount >= 50 && newCount > prevCount) {
                  handleInputChange({
                    toEmails: {
                      ...formData.toEmails,
                      errors: { validEmailList: 'You can send to a maximum of 50 email addresses.' },
                    },
                  });
                  return;
                }
                const strictEmailRe = /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                const rawEntries = val.split(',').map((s) => s.trim());
                const nonEmptyEmails = rawEntries.filter(Boolean);
                let emailError = null;
                if (nonEmptyEmails.length > 50) {
                  emailError = 'You can send to a maximum of 50 email addresses.';
                } else if (rawEntries.some((em) => !strictEmailRe.test(em))) {
                  emailError = 'Invalid email format found in the list.';
                }
                handleInputChange({
                  toEmails: {
                    ...formData.toEmails,
                    value: val,
                    errors: emailError ? { validEmailList: emailError } : {},
                  },
                });
              }}
              error={Boolean(toEmailsErr)}
              variant='standard'
            />
            {toEmailsErr && <FormHelperText error>{toEmailsErr}</FormHelperText>}
          </div>
        </div>
      )}

      {recipientMode === 'Guest Segments' && (
        <EmailServiceAudienceTab
          formData={guestFormData}
          handleInputChange={handleInputChange}
          setFormData={setFormData}
          originalRoomList={roomList}
          originalRoomTypeList={roomTypeList}
          hotelId={hotelId}
          language={language}
          isReadOnly={false}
          hideRecipientToggle
          singleAudienceType
        />
      )}
    </div>
  );
};

const DRAWER_DEFAULT = '1000px';
const DRAWER_CANVAS  = 'min(100vw, 1280px)';

const NEW_MESSAGE_TEMPLATE_MODULES = ['elevate_instant_messaging'];

const NewMessageCreate = ({ onClose, hotelId, groupId, defaultFromAddress, onWidthChange, onSent }) => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const abortControllersRef = useRef({});
  const roomList = useSelector(allRoomsSelector) || [];
  const roomTypeList = useSelector(allRoomTypesSelector) || [];
  const [localDrawerWidth, setLocalDrawerWidth] = useState(DRAWER_DEFAULT);
  const [isTemplateCanvasValid, setIsTemplateCanvasValid] = useState(false);
  const templateTabRef = React.useRef(null);

  useEffect(() => {
    if (tabValue === 1 && hotelId) {
      dispatch(fetchAllAccommodations(hotelId));
    }
  }, [tabValue, hotelId, dispatch]);

  const handleAttach = (file) => {
    const currentTotal = attachments
      .filter((a) => !a.error)
      .reduce((sum, a) => sum + (a.size || 0), 0);
    if (currentTotal + file.size > MAX_EMAIL_ATTACHMENT_SIZE) {
      notificationManager('danger', 'Total attachment size cannot exceed 8 MB.');
      return;
    }

    const id = `${Date.now()}-${file.name}`;
    const controller = new AbortController();
    abortControllersRef.current[id] = controller;

    setAttachments((prev) => [
      ...prev,
      { id, name: file.name, size: file.size, key: null, progress: 0, uploading: true, error: false },
    ]);

    uploadPdfToS3(
      file,
      groupId,
      (pct) =>
        setAttachments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, progress: pct } : a)),
        ),
      controller.signal,
    )
      .then((key) => {
        setAttachments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, key, progress: 100, uploading: false } : a)),
        );
      })
      .catch(() => {
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === id ? { ...a, uploading: false, error: true, progress: 0 } : a,
          ),
        );
        notificationManager('danger', `Failed to upload ${file.name}. Please try again.`);
      })
      .finally(() => {
        delete abortControllersRef.current[id];
      });
  };

  const handleRemoveAttach = (id) => {
    if (abortControllersRef.current[id]) {
      abortControllersRef.current[id].abort();
      delete abortControllersRef.current[id];
    }
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const notifyWidth = (width) => {
    setLocalDrawerWidth(width);
    onWidthChange && onWidthChange(width);
  };

  const [formData, setFormData] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    buildInitialFormData(defaultFromAddress),
  );

  const handleInputChange = (updates) => {
    setFormData(updates);
    if (updates?.templateMode !== undefined) {
      const mode = updates.templateMode?.value ?? '';
      notifyWidth(mode === 'Template' ? DRAWER_CANVAS : DRAWER_DEFAULT);
    }
  };

  const templateMode = formData?.templateMode?.value || '';

  const handleBackFromTemplate = () => {
    setFormData({
      templateMode: { ...formData.templateMode, value: '' },
      htmlTemplateBody: { ...formData.htmlTemplateBody, value: '' },
      subject: { ...formData.subject, value: '', errors: {} },
      subjectBody: { ...formData.subjectBody, value: '' },
    });
    notifyWidth(DRAWER_DEFAULT);
  };

  const validateFields = (fields) => {
    const slice = Object.fromEntries(Object.entries(formData).filter(([k]) => fields.includes(k)));
    const updated = {};
    const ok = formDataValidate(slice, updated);
    if (!ok) setFormData(updated);
    return ok;
  };

  const validateTemplateTab = () => {
    if (!templateMode) {
      notificationManager('danger', 'Please provide a valid template content before proceeding.');
      return false;
    }
    if (templateMode === 'Custom') {
      return validateFields(['subject', 'subjectBody']);
    }
    if (templateMode === 'Template') {
      if (!validateFields(['subject'])) return false;
      if (!isTemplateCanvasValid && !formData?.htmlTemplateBody?.value?.trim()) {
        notificationManager('warning', 'Please complete the email template and click Next in the canvas.');
        return false;
      }
    }
    return true;
  };

  const validateAudienceTab = () => {
    const recipientMode = formData?.recipientMode?.value || 'Custom';
    if (recipientMode === 'Custom') {
      if (!validateFields(['toEmails'])) return false;
      const emailCount = (formData?.toEmails?.value || '')
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean).length;
      if (emailCount > 50) {
        setFormData({
          toEmails: {
            ...formData.toEmails,
            errors: { validEmailList: 'You can send to a maximum of 50 email addresses.' },
          },
        });
        return false;
      }
    }
    if (recipientMode === 'Guest Segments') {
      const selectedTypes = formData?.audienceType?.value || [];
      if (!selectedTypes.length) {
        setFormData({ audienceType: { ...formData.audienceType, errors: { required: 'Please select at least one Audience Type.' } } });
        return false;
      }
      const fieldMap = { Rooms: 'residentRoom', 'Room Type': 'roomType', 'Block Code': 'blockCodes', 'Loyalty Tier': 'loyaltyTiers' };
      for (const type of selectedTypes) {
        const key = fieldMap[type];
        if (key && !(formData?.[key]?.value?.length)) {
          return false;
        }
      }
    }
    return true;
  };

  const handleTabChange = (_, newValue) => {
    if (newValue > tabValue && tabValue === 0) {
      if (!validateTemplateTab()) return;
    }
    if (newValue !== 0) notifyWidth(DRAWER_DEFAULT);
    setTabValue(newValue);
  };

  const handleSend = async () => {
    if (!validateTemplateTab()) { setTabValue(0); return; }
    if (!validateAudienceTab()) return;
    setSending(true);
    setSendError(false);
    try {
      const loggedUser = getLoggedInUser();
      const createdBy = loggedUser?.firstName
        ? `${loggedUser.firstName} ${loggedUser.lastName || ''}`.trim()
        : (loggedUser?.email || '').split('@')[0];

      const readyAttachments = attachments.filter((a) => a.key && !a.error);

      const totalAttachmentBytes = readyAttachments.reduce((sum, a) => sum + (a.size || 0), 0);
      if (totalAttachmentBytes > MAX_EMAIL_ATTACHMENT_SIZE) {
        notificationManager('danger', 'Total attachment size cannot exceed 8 MB.');
        setSending(false);
        return;
      }

      const attachmentKeys = readyAttachments.map((a) => a.key);

      const rawBody =
        templateMode === 'Custom'
          ? wrapBodyAsHtml(formData?.subjectBody?.value || '')
          : formData?.htmlTemplateBody?.value || '';
      const body =
        rawBody.trimStart().startsWith('<!DOCTYPE') || rawBody.trimStart().startsWith('<html')
          ? rawBody
          : `<!DOCTYPE html><html><body>${rawBody}</body></html>`;

      const audience = buildAudience(formData, roomTypeList);

      await sendEmail({
        hotelId,
        fromAddress: formData.fromAddress?.value || '',
        subject: formData.subject?.value || '',
        body: encodeURIComponent(body),
        audience,
        cc: formData.cc?.value || '',
        attachmentKeys,
        createdBy,
      });

      notificationManager('success', 'Email sent successfully.');
      onSent?.();
      onClose();
    } catch (e) {
      setSendError(true);
      const apiMsg = e?.graphQLErrors?.[0]?.message || e?.message || '';
      const userMsg = apiMsg.includes('Recipient count exceeds')
        ? 'You can send to a maximum of 50 email addresses. Please reduce the recipients and try again.'
        : apiMsg.includes('guest segment')
        ? 'Guest Segment targeting is not yet supported. Please use Custom (explicit emails) to send.'
        : 'Failed to Send Email. Please Try Again.';
      notificationManager('danger', userMsg);
    } finally {
      setSending(false);
    }
  };

  const renderFooter = () => {
    if (tabValue === 1) {
      const recipientMode = formData?.recipientMode?.value || 'Custom';
      const toEmailsValue = (formData?.toEmails?.value || '').trim();
      const toEmailsErrors = formData?.toEmails?.errors || {};
      const toEmailsHasError = !!(toEmailsErrors.required || toEmailsErrors.validEmailList);
      const ccHasError = !!(formData?.cc?.errors?.validEmailList);
      const anyUploading = attachments.some((a) => a.uploading);
      const selectedAudienceTypes = formData?.audienceType?.value || [];
      const audienceFieldMap = { Rooms: 'residentRoom', 'Room Type': 'roomType', 'Block Code': 'blockCodes', 'Loyalty Tier': 'loyaltyTiers' };
      const audienceSubFieldsFilled =
        selectedAudienceTypes.length > 0 &&
        selectedAudienceTypes.every((type) => {
          const key = audienceFieldMap[type];
          return key ? (formData?.[key]?.value || []).length > 0 : true;
        });
      const isSendReady =
        !anyUploading &&
        !ccHasError &&
        (recipientMode === 'Custom'
          ? toEmailsValue.length > 0 && !toEmailsHasError
          : audienceSubFieldsFilled);
      return (
        <div className='nm-footer'>
          <Button
            variant='contained'
            color='primary'
            size='large'
            onClick={handleSend}
            disabled={sending || !isSendReady}
          >
            {sending ? 'Sending...' : sendError ? 'Retry' : 'Send'}
          </Button>
          <Button variant='outlined' color='secondary' size='large' onClick={onClose}>
            Cancel
          </Button>
        </div>
      );
    }
    if (tabValue === 0 && templateMode === 'Custom') {
      const isNextReady =
        (formData?.subject?.value || '').trim().length > 0 &&
        (formData?.subjectBody?.value || '').trim().length > 0;
      return (
        <div className='nm-footer'>
          <Button
            variant='contained'
            color='primary'
            size='large'
            disabled={!isNextReady}
            onClick={() => { if (validateTemplateTab()) setTabValue(1); }}
          >
            Next
          </Button>
          <Button variant='outlined' color='secondary' size='large' onClick={onClose}>
            Cancel
          </Button>
        </div>
      );
    }
    return null;
  };

  const isCanvasMode = tabValue === 0 && templateMode === 'Template';
  const footer = renderFooter();

  return (
    <div className='nm-drawer-root' style={{ width: localDrawerWidth }}>
      <div
        className={
          isCanvasMode
            ? 'nm-drawer-content d-flex align-items-center flex-column service-container'
            : 'nm-drawer-content container d-flex align-items-center flex-column service-container'
        }
      >
        <div className='row px-3 w-100 mt-4 align-items-center'>
          <div className='col-auto d-flex align-items-center'>
            <span className='page-title me-3'>New Message</span>
          </div>
          <div className='col d-flex align-items-center justify-content-end gap-3'>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor='primary'
              textColor='primary'
              className='property-tabs'
              aria-label='new-message-tabs'
            >
              <Tab label='Template' />
              <Tab label='Audience' />
            </Tabs>
            <IconButton aria-label='close' onClick={onClose} className='ms-2'>
              <CancelIcon />
            </IconButton>
          </div>
        </div>

        {tabValue === 0 && (
          <div className={isCanvasMode ? 'w-100 px-3 mt-3' : 'row px-3 w-100 d-flex mt-3'}>
            {templateMode === 'Custom' ? (
              <CustomComposeForm
                formData={formData}
                handleInputChange={handleInputChange}
                onBack={handleBackFromTemplate}
                attachments={attachments}
                onAttach={handleAttach}
                onRemoveAttach={handleRemoveAttach}
              />
            ) : (
              <EmailServiceTemplateTab
                formData={formData}
                handleInputChange={handleInputChange}
                onNext={() => {
                  notifyWidth(DRAWER_DEFAULT);
                  setTabValue(1);
                }}
                onSave={() => {}}
                onStageChange={() => {}}
                onValidationChange={(valid) => setIsTemplateCanvasValid(valid)}
                registerHistoryApi={(api) => { templateTabRef.current = api; }}
                isViewOnly={false}
                isEditing={false}
                templateListModules={NEW_MESSAGE_TEMPLATE_MODULES}
              />
            )}
          </div>
        )}

        {tabValue === 1 && (
          <div className='row px-3 w-100 d-flex mt-3'>
            <NewMessageAudienceTab
              formData={formData}
              handleInputChange={handleInputChange}
              setFormData={setFormData}
              hotelId={hotelId}
              roomList={roomList}
              roomTypeList={roomTypeList}
            />
          </div>
        )}
      </div>

      {footer && (
        <div className='nm-drawer-footer px-3'>
          {footer}
        </div>
      )}
    </div>
  );
};

export default NewMessageCreate;
